import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, Mail, Phone, MessageSquare } from "lucide-react";
import { Header } from "@/components/ui/header";

interface NoClientAccessProps {
  userEmail: string;
}

export function NoClientAccess({ userEmail }: NoClientAccessProps) {
  const supportEmail = "info@westtn.consulting";

  return (
    <div className="min-h-screen bg-card-50">
      <Header />
      <div className="container mx-auto p-2 pt-20">
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-lg">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 p-3 bg-yellow-100 rounded-full w-16 h-16 flex items-center justify-center dark:bg-yellow-900/40">
                <AlertCircle className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
              </div>
              <CardTitle className="text-2xl text-card-foreground">
                No Client Access Found
              </CardTitle>
              <CardDescription className="text-lg">
                We couldn&apos;t find any client accounts associated with your email address.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 dark:bg-blue-950/40 dark:border-blue-800">
                <h3 className="font-semibold text-blue-900 mb-2 dark:text-blue-200">What happened?</h3>
                <p className="text-blue-800 text-sm dark:text-blue-200">
                  Your account ({userEmail}) is not currently associated with any client accounts in
                  our system. This usually happens when:
                </p>
                <ul className="text-blue-800 text-sm mt-2 space-y-1 list-disc list-inside dark:text-blue-200">
                  <li>Your client account hasn&apos;t been set up yet</li>
                  <li>Your email address needs to be linked to your client account</li>
                  <li>There was an issue with your account configuration</li>
                </ul>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 dark:bg-green-950/40 dark:border-green-800">
                <h3 className="font-semibold text-green-900 mb-2 dark:text-green-200">How to get help</h3>
                <p className="text-green-800 text-sm mb-3 dark:text-green-200">
                  Please contact our support team to resolve this issue. When you reach out, please
                  mention:
                </p>
                <div className="bg-card rounded p-3 border">
                  <p className="text-sm font-mono text-gray-700 dark:text-gray-300">Email: {userEmail}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  variant="outline"
                  className="flex items-center justify-center space-x-2"
                  onClick={() =>
                    (window.location.href = `mailto:${supportEmail}?subject=Client Access Issue - ${userEmail}&body=Hello,%0D%0A%0D%0AI'm having trouble accessing my client dashboard. My email address is: ${userEmail}%0D%0A%0D%0APlease help me get access to my client account.%0D%0A%0D%0AThank you.`)
                  }
                >
                  <Mail className="h-4 w-4" />
                  <span>Email Support</span>
                </Button>

                <Button
                  variant="outline"
                  className="flex items-center justify-center space-x-2"
                  onClick={() => (window.location.href = "tel:+19016131510")}
                >
                  <Phone className="h-4 w-4" />
                  <span>(901) 613-1510</span>
                </Button>

                <Button
                  variant="outline"
                  className="flex items-center justify-center space-x-2"
                  onClick={() => window.open("https://westtn.consulting/contact", "_blank")}
                >
                  <MessageSquare className="h-4 w-4" />
                  <span>Contact Form</span>
                </Button>
              </div>

              <div className="text-center text-sm text-gray-500">
                <p>Support Hours: Monday - Friday, 8:00 AM - 6:00 PM CST</p>
                <p className="mt-1">Emergency support available 24/7 for critical issues</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
